import { err, ok, type Result } from "./result";
import type { RichTextDoc } from "./richText";

export type DefinedTermCreateDraft<LegislationId extends string = string> = {
  readonly legislationId: LegislationId;
  readonly term: string;
  readonly definition: RichTextDoc;
  readonly caseSensitive?: boolean;
};

export type PreparedDefinedTermCreate<LegislationId extends string = string> = {
  readonly legislationId: LegislationId;
  readonly term: string;
  readonly definition: RichTextDoc;
  readonly caseSensitive: boolean;
};

export type DefinedTerm = {
  readonly text: string;
  readonly caseSensitive: boolean;
};

export type DefinedTermMap = ReadonlyMap<string, DefinedTerm>;

export type DefinedTermTextSegment =
  | { readonly kind: "text"; readonly text: string }
  | {
      readonly kind: "definedTerm";
      readonly text: string;
      readonly definition: {
        readonly term: string;
        readonly text: string;
      };
    };

export type DefinedTermError = { readonly kind: "missingTerm" };

export type DefinedTermPopoverPlacementInput = {
  readonly anchorX: number;
  readonly anchorY: number;
  readonly popoverWidth: number;
  readonly viewportWidth: number;
  readonly margin?: number;
  readonly offsetY?: number;
};

export type DefinedTermPopoverPlacement = {
  readonly left: number;
  readonly top: number;
  readonly width: number;
};

export const definedTermErrorMessage = (error: DefinedTermError): string => {
  switch (error.kind) {
    case "missingTerm":
      return "Defined term is required";
  }
};

export const normalizeDefinedTermText = (term: string): string =>
  term.trim().replace(/\s+/g, " ");

export const selectedDefinedTerm = (term: string): Result<string, DefinedTermError> => {
  const cleaned = normalizeDefinedTermText(term);
  return cleaned === "" ? err({ kind: "missingTerm" }) : ok(cleaned);
};

export const prepareDefinedTermCreate = <LegislationId extends string>(
  draft: DefinedTermCreateDraft<LegislationId>,
): Result<PreparedDefinedTermCreate<LegislationId>, DefinedTermError> => {
  const term = selectedDefinedTerm(draft.term);
  if (!term.ok) return term;

  return ok({
    legislationId: draft.legislationId,
    term: term.value,
    definition: draft.definition,
    caseSensitive: draft.caseSensitive ?? false,
  });
};

export const definedTermPopoverPlacement = ({
  anchorX,
  anchorY,
  popoverWidth,
  viewportWidth,
  margin = 16,
  offsetY = 8,
}: DefinedTermPopoverPlacementInput): DefinedTermPopoverPlacement => {
  const width = Math.min(popoverWidth, Math.max(0, viewportWidth - margin * 2));
  const availableRight = viewportWidth - width - margin;
  const left = Math.max(margin, Math.min(anchorX - width / 2, availableRight));
  return { left, top: anchorY + offsetY, width };
};

type IndexedDefinedTerm = { readonly term: string } & DefinedTerm;

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const definedTermPattern = (terms: DefinedTermMap): RegExp =>
  new RegExp(
    `\\b(${[...terms.keys()].map(escapeRegExp).sort((a, b) => b.length - a.length).join("|")})\\b`,
    "gi",
  );

const lowerCaseDefinedTermIndex = (terms: DefinedTermMap): ReadonlyMap<string, IndexedDefinedTerm> => {
  const byLower = new Map<string, IndexedDefinedTerm>();
  for (const [term, definition] of terms) byLower.set(term.toLowerCase(), { term, ...definition });
  return byLower;
};

const definedTermEntryForMatch = (
  matchedText: string,
  terms: DefinedTermMap,
  byLower: ReadonlyMap<string, IndexedDefinedTerm>,
): IndexedDefinedTerm | null => {
  const exact = terms.get(matchedText);
  if (exact !== undefined) return { term: matchedText, ...exact };

  const lower = byLower.get(matchedText.toLowerCase());
  return lower !== undefined && !lower.caseSensitive ? lower : null;
};

export const definedTermTextSegments = (
  text: string,
  terms: DefinedTermMap,
): DefinedTermTextSegment[] => {
  if (text === "") return [];
  if (terms.size === 0) return [{ kind: "text", text }];

  const byLower = lowerCaseDefinedTermIndex(terms);
  const pattern = definedTermPattern(terms);
  const segments: DefinedTermTextSegment[] = [];
  let last = 0;

  for (const match of text.matchAll(pattern)) {
    const index = match.index ?? 0;
    const matchedText = match[0];
    const entry = definedTermEntryForMatch(matchedText, terms, byLower);
    if (entry === null) continue;

    if (index > last) segments.push({ kind: "text", text: text.slice(last, index) });
    segments.push({
      kind: "definedTerm",
      text: matchedText,
      definition: {
        term: entry.term,
        text: entry.text,
      },
    });
    last = index + matchedText.length;
  }

  if (last < text.length) segments.push({ kind: "text", text: text.slice(last) });
  return segments.length === 0 ? [{ kind: "text", text }] : segments;
};

